import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  ReactNode,
  useDeferredValue,
} from 'react';

import type { SortDirection } from '@/shared/types/common';

export type BroadcastSortDirection = Exclude<SortDirection, null>;

export type FilterStatus = 'draft' | 'ready' | 'in_progress' | 'sent' | 'failed' | 'all';

export type ApiStatusFilter = Exclude<FilterStatus, 'all'>;

export interface BroadcastFilterContextType {
  status: FilterStatus;
  apiStatus?: ApiStatusFilter;
  onStatusChange: (newFilter: FilterStatus) => void;
  textFilter?: string;
  deferredTextFilter?: string;
  debouncedTextFilter?: string;
  setTextFilter: (text?: string) => void;
  sortDirection: BroadcastSortDirection;
  toggleSortDirection: () => void;
}

const BroadcastFilterContext = createContext<BroadcastFilterContextType | undefined>(undefined);

interface BroadcastFilterProviderProps {
  children: ReactNode;
}

export const BroadcastFilterProvider: React.FC<BroadcastFilterProviderProps> = ({ children }) => {
  const [status, setStatus] = useState<FilterStatus>('all');
  const [textFilter, setTextFilter] = useState<string>();
  const [sortDirection, setSortDirection] = useState<BroadcastSortDirection>('desc');
  const deferredTextFilter = useDeferredValue(textFilter);

  const apiStatus = useMemo(
    () => (status === 'all' ? undefined : (status as ApiStatusFilter)),
    [status]
  );

  const onStatusChange = useCallback((newFilter: FilterStatus) => {
    setStatus(newFilter);
  }, []);

  const handleSetTextFilter = useCallback((text?: string) => {
    setTextFilter(text);
  }, []);

  const toggleSortDirection = useCallback(() => {
    setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  }, []);

  const value = useMemo(
    () => ({
      status,
      apiStatus,
      onStatusChange,
      textFilter,
      deferredTextFilter,
      setTextFilter: handleSetTextFilter,
      sortDirection,
      toggleSortDirection,
    }),
    [
      status,
      apiStatus,
      onStatusChange,
      textFilter,
      deferredTextFilter,
      handleSetTextFilter,
      sortDirection,
      toggleSortDirection,
    ]
  );

  return (
    <BroadcastFilterContext.Provider value={value}>{children}</BroadcastFilterContext.Provider>
  );
};

export const useBroadcastFilter = (): BroadcastFilterContextType => {
  const context = useContext(BroadcastFilterContext);

  if (context === undefined) {
    // This prevents the app from breaking when the hook is used in components
    // that are not wrapped by BroadcastFilterProvider
    return {} as BroadcastFilterContextType;
  }

  return context;
};
